import { useCallback, useEffect, useRef, type ComponentType } from 'react';
import { useRecordContext } from 'react-admin';

/**
 * Panel, którego stan należy do jednego rekordu.
 *
 * React-admin przy przejściu z `#/user-offers/17629/show` na
 * `#/user-offers/18099/show` nie tworzy strony od nowa — ten sam `Show`
 * dostaje nowy rekord, a panele pod nim zachowują swój `useState`. Panel
 * podpowiedzi pokazywał więc na 18099 auta policzone dla 17629, z przyciskiem
 * „Przelicz ponownie” (błąd z produkcji, 28.09.2026).
 *
 * Owijka daje panelowi `key` równy id rekordu: nowy rekord = nowy egzemplarz
 * panelu z czystym stanem. Stary egzemplarz jest odmontowany, więc odpowiedź,
 * która przyjdzie dla poprzedniego zlecenia już po przejściu, trafia w próżnię
 * — nie nadpisze wyniku bieżącego rekordu.
 */
export function recordScoped<P extends object>(Panel: ComponentType<P>): ComponentType<P> {
  const Scoped = (props: P) => {
    const record = useRecordContext();
    return <Panel key={record?.id === undefined ? '' : String(record.id)} {...props} />;
  };
  Scoped.displayName = `recordScoped(${Panel.displayName ?? Panel.name ?? 'Panel'})`;
  return Scoped;
}

/**
 * Czy ten egzemplarz panelu jest jeszcze na ekranie. W panelu owiniętym
 * `recordScoped` znaczy to tyle co „odpowiedź dotyczy bieżącego rekordu” —
 * pozwala nie pokazywać komunikatu o błędzie zapytania zadanego dla
 * zlecenia, z którego planista już wyszedł.
 */
export function useStillMounted(): () => boolean {
  const mounted = useRef(false);
  useEffect(() => {
    // Ustawiane w efekcie, nie tylko przy tworzeniu: StrictMode montuje
    // komponent dwukrotnie i sprzątanie pierwszego montażu gasi flagę.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return useCallback(() => mounted.current, []);
}
